import type {
  GlobalHierarchyFilters,
  GlobalHierarchyFiltersUpdate,
  GlobalHierarchyMatchingOption,
} from '../api/@types/hierarchy.types';

export interface GlobalHierarchyRecordFilter {
  /** Raw URL chunk of repeated `&filter=globalHierarchy:...` clauses, or undefined when unscoped. */
  filter?: string;
  /** `all` or `any`, matching the records API `globalHierarchyMatch` param. */
  match?: 'all' | 'any';
}

/** Reads the matching option (string or object-shaped) from a saved scope. */
const readMatchingOption = (data?: GlobalHierarchyFilters): GlobalHierarchyMatchingOption | undefined =>
  typeof data?.hierarchyMatchingOption === 'object'
    ? data.hierarchyMatchingOption?.value
    : data?.hierarchyMatchingOption;

/**
 * Groups the saved scope (a flat array of hierarchy values) into selected value ids keyed by
 * hierarchy type id. Returns an empty object when nothing is scoped.
 */
export const groupSelectedByType = (data?: GlobalHierarchyFilters): Record<number, number[]> => {
  const selected = data?.globalHierarchyFilters;
  if (!Array.isArray(selected)) {
    return {};
  }

  const idsByType: Record<number, number[]> = {};
  for (const value of selected) {
    if (value?.id == null || value.hierarchyTypeId == null) {
      continue;
    }
    if (!idsByType[value.hierarchyTypeId]) {
      idsByType[value.hierarchyTypeId] = [];
    }
    idsByType[value.hierarchyTypeId].push(value.id);
  }
  return idsByType;
};

/**
 * Translates the user's saved global hierarchy scope into the record-query parameters used by the
 * records API, mirroring how the web app filters record lists by global hierarchy.
 *
 * Produces repeated `&filter=globalHierarchy:{typeId}:id:in:{ids}` clauses plus the matching option.
 * Returns an empty object when nothing is scoped, so the picker stays unscoped.
 */
export const buildGlobalHierarchyRecordFilter = (data?: GlobalHierarchyFilters): GlobalHierarchyRecordFilter => {
  const idsByType = groupSelectedByType(data);
  const entries = Object.entries(idsByType);
  if (entries.length === 0) {
    return {};
  }

  let filter = '';
  for (const [typeId, ids] of entries) {
    filter += `&filter=globalHierarchy:${typeId}:id:in:${ids.join(',')}`;
  }

  return { filter, match: readMatchingOption(data) === 'MATCH_ALL' ? 'all' : 'any' };
};

/**
 * Builds the PUT `/global-hierarchy-filters` body from the editor's selections — a flat list of the
 * selected value ids plus the matching option.
 */
export const buildGlobalHierarchyFiltersBody = (
  selectedByType: Record<number, number[]>,
  matchingOption: GlobalHierarchyMatchingOption
): GlobalHierarchyFiltersUpdate => ({
  globalHierarchyFilters: Object.values(selectedByType)
    .flat()
    .map((id) => ({ id })),
  hierarchyMatchingOption: matchingOption,
});
